import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateAdmin } from '../../store/slices/adminSlice';
import { addNotification } from '../../store/slices/uiSlice';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

const MODULES = [
  { key: 'dashboard' },
  { key: 'admins' },
  { key: 'members' },
  { key: 'events' },
  { key: 'attendance_records' },
  { key: 'attendance_checkin' },
  { key: 'tithe_receipts' },
  { key: 'forms' },
  { key: 'posts' },
  { key: 'joomla' }
];

const PERMISSIONS = [
  { key: 'can_create', nameKey: 'create', color: 'text-green-600' },
  { key: 'can_read', nameKey: 'read', color: 'text-blue-600' },
  { key: 'can_update', nameKey: 'update', color: 'text-yellow-600' },
  { key: 'can_delete', nameKey: 'delete', color: 'text-red-600' },
  { key: 'can_manage', nameKey: 'manage', color: 'text-purple-600' }
];

const AdminModal = ({ admin, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('edit');
  
  // Edit form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    is_active: true,
  });
  
  // Permissions state
  const [adminPermissions, setAdminPermissions] = useState({});
  
  const [loading, setLoading] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);

  useEffect(() => {
    if (admin) {
      // Initialize form data
      setFormData({
        username: admin.username || '',
        email: admin.email || '',
        first_name: admin.first_name || '',
        last_name: admin.last_name || '',
        is_active: admin.is_active !== undefined ? admin.is_active : true,
      });
      
      // Initialize permissions data
      const permissions = {};
      MODULES.forEach(module => {
        permissions[module.key] = admin.permissions?.[module.key] || {
          can_create: false,
          can_read: false,
          can_update: false,
          can_delete: false,
          can_manage: false
        };
      });
      setAdminPermissions(permissions);
    }
  }, [admin]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePermissionChange = (module, permission, checked) => {
    setAdminPermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [permission]: checked
      }
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {};
      
      // Only include changed fields
      if (formData.username !== admin.username) updateData.username = formData.username;
      if (formData.email !== admin.email) updateData.email = formData.email;
      if (formData.first_name !== admin.first_name) updateData.first_name = formData.first_name;
      if (formData.last_name !== admin.last_name) updateData.last_name = formData.last_name;
      if (formData.is_active !== admin.is_active) updateData.is_active = formData.is_active;

      if (Object.keys(updateData).length === 0) {
        dispatch(addNotification({
          type: 'info',
          title: t('admin.modal.noChangesTitle'),
          message: t('admin.modal.noChangesMsg'),
        }));
        return;
      }

      await dispatch(updateAdmin({ id: admin.id, adminData: updateData })).unwrap();
      
      dispatch(addNotification({
        type: 'success',
        title: 'Success!',
        message: t('admin.modal.notifications.updateSuccess'),
      }));
      
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Error!',
        message: error.message || t('admin.modal.notifications.updateError'),
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    setSavingPermissions(true);
    try {
      await api.put(`/api/v1/admins/${admin.id}/permissions`, adminPermissions);

      dispatch(addNotification({
        type: 'success',
        title: 'Success!',
        message: t('admin.modal.notifications.permsSuccess')
      }));

      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Error!',
        message: t('admin.modal.notifications.permsError')
      }));
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleSaveAndClose = async () => {
    if (activeTab === 'edit') {
      await handleEditSubmit({ preventDefault: () => {} });
    } else {
      await handleSavePermissions();
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            {t('admin.modal.title', { username: admin?.username })}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('edit')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'edit'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span>{t('admin.modal.tabs.edit')}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('permissions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'permissions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>{t('admin.modal.tabs.permissions')}</span>
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === 'edit' ? (
            /* Edit Admin Tab */
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.modal.fields.username')}
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleFormChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.modal.fields.email')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.modal.fields.firstName')}
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.modal.fields.lastName')}
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleFormChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                  {t('admin.modal.fields.active')}
                </label>
              </div>
            </form>
          ) : (
            /* Permissions Tab */
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex">
                  <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">{t('admin.modal.info.title')}</p>
                    <p>{t('admin.modal.info.description')}</p>
                  </div>
                </div>
              </div>

              <div className="grid gap-6">
                {MODULES.map(module => (
                  <div key={module.key} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      {t(`admin.modal.modules.${module.key}`)}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {PERMISSIONS.map(permission => (
                        <label key={permission.key} className="flex items-center p-2 rounded-md hover:bg-white transition-colors">
                          <input
                            type="checkbox"
                            checked={adminPermissions[module.key]?.[permission.key] || false}
                            onChange={(e) => handlePermissionChange(module.key, permission.key, e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                          />
                          <span className={`text-sm font-medium ${permission.color}`}>
                            {t(`admin.modal.permissions.${permission.nameKey}`)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {t('admin.modal.cancel')}
          </button>
          <button
            onClick={handleSaveAndClose}
            disabled={loading || savingPermissions}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading || savingPermissions ? t('admin.modal.saving') : t('admin.modal.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminModal;
